/**
 * @file ModalComponent.tsx
 * @description
 * -----------------------------------------------------
 * Componente modal
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-01-14
 * @modified 2025-09-26
 * @author DLTCode
 * @author Sergio Manjón
 * @author Ismael Benabdellah
 * @author Pedro Luis Gómez
 * @author Nacho Moya
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { c as _c } from "react/compiler-runtime";
import { createPortal } from "react-dom";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import Form from "next/form";
import { motion } from "framer-motion";
import ButtonComponent from "@/components/ui/ButtonComponent";
import { ModalComponentProps } from "@/types/modals/modals";
import { X, PencilLine } from "lucide-react";
import { FocusTrap } from "focus-trap-react";
import SubmitButtonComponent from "../ui/SubmitButtonComponent";
const ModalComponent = t0 => {
  const $ = _c(88);
  const {
    id,
    title,
    isOpen,
    onClose,
    children,
    onConfirm,
    loadingConfirm,
    disabledConfirm,
    formAction,
    customButton,
    buttonCancel: t1,
    buttonConfirmText: t2,
    buttonCancelText: t3,
    buttonSubmitText: t4,
    submitIcon: t5,
    hideSubmitIcon,
    avoidCloseOnSubmit: t6,
    titleAndAria,
    style,
    bodyStyle,
    buttonConfirmClassname,
    hideRequiredFieldsMessage: t7
  } = t0;
  const buttonCancel = t1 === undefined ? false : t1;
  const buttonConfirmText = t2 === undefined ? "buttonConfirm" : t2;
  const buttonCancelText = t3 === undefined ? "buttonCancel" : t3;
  const buttonSubmitText = t4 === undefined ? "buttonSubmit" : t4;
  const submitIcon = t5 === undefined ? PencilLine : t5;
  const avoidCloseOnSubmit = t6 === undefined ? false : t6;
  const hideRequiredFieldsMessage = t7 === undefined ? false : t7;
  const button = useTranslations("Buttons");
  const modals = useTranslations("Modals");
  const t = useTranslations("DialogComponent");
  const previouslyFocusedElement = useRef(null);
  const modalBodyRef = useRef(null);
  const reactId = useId();
  let t8;
  if ($[0] !== reactId) {
    let t9;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = /[:]/g;
      $[2] = t9;
    } else {
      t9 = $[2];
    }
    t8 = reactId.replace(t9, "");
    $[0] = reactId;
    $[1] = t8;
  } else {
    t8 = $[1];
  }
  const titleId = `modal-title-${t8}`;
  const [hasFormWithRequiredFields, setHasFormWithRequiredFields] = useState(false);
  let t9;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const modalBody = modalBodyRef.current;
      if (!modalBody) {
        setHasFormWithRequiredFields(false);
        return;
      }
      const formElement = modalBody.querySelector("form");
      const hasRequiredFields = Boolean(formElement?.querySelector("[label--required], [aria-required='true']"));
      setHasFormWithRequiredFields(Boolean(formElement) && hasRequiredFields);
    };
    $[3] = t9;
  } else {
    t9 = $[3];
  }
  const evaluateRequiredFieldsMessage = t9;
  const shouldShowRequiredFieldsMessage = hasFormWithRequiredFields;
  let t10;
  if ($[4] !== onClose) {
    t10 = () => {
      onClose();
      if (previouslyFocusedElement.current) {
        previouslyFocusedElement.current.focus();
      }
    };
    $[4] = onClose;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  const handleClose = t10;
  const [mounted, setMounted] = useState(false);
  const [isTrapPaused, setIsTrapPaused] = useState(false);
  const trapPausedRef = useRef(false);
  const dialogRef = useRef(null);
  let t11;
  if ($[6] !== avoidCloseOnSubmit || $[7] !== onClose || $[8] !== onConfirm) {
    t11 = async e => {
      if (onConfirm) {
        await onConfirm(e);
      }
      if (!avoidCloseOnSubmit) {
        onClose();
        if (previouslyFocusedElement.current) {
          previouslyFocusedElement.current.focus();
        }
      }
    };
    $[6] = avoidCloseOnSubmit;
    $[7] = onClose;
    $[8] = onConfirm;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  const handleConfirm = t11;
  let t12;
  let t13;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      setMounted(true);
      return () => setMounted(false);
    };
    t13 = [];
    $[10] = t12;
    $[11] = t13;
  } else {
    t12 = $[10];
    t13 = $[11];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[12] !== isOpen) {
    t14 = () => {
      const setTrapPausedIfChanged = nextValue => {
        if (trapPausedRef.current === nextValue) {
          return;
        }
        trapPausedRef.current = nextValue;
        setIsTrapPaused(nextValue);
      };
      if (!isOpen) {
        setTrapPausedIfChanged(false);
        return;
      }
      const syncTrapStateWithRadixPortal = () => {
        const hasRadixPortal = Boolean(document.body.querySelector("[data-radix-popper-content-wrapper]"));
        setTrapPausedIfChanged(hasRadixPortal);
      };
      syncTrapStateWithRadixPortal();
      const observer = new MutationObserver(syncTrapStateWithRadixPortal);
      observer.observe(document.body, {
        childList: true
      });
      return () => {
        observer.disconnect();
        setTrapPausedIfChanged(false);
      };
    };
    t15 = [isOpen];
    $[12] = isOpen;
    $[13] = t14;
    $[14] = t15;
  } else {
    t14 = $[13];
    t15 = $[14];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[15] !== isOpen || $[16] !== mounted) {
    t16 = () => {
      if (!isOpen || !mounted) {
        return;
      }
      const modalElement = dialogRef.current;
      if (!modalElement) {
        return;
      }
      if (!modalElement.open) {
        try {
          modalElement.show();
        } catch {}
      }
      return () => {
        if (modalElement.open) {
          modalElement.close();
        }
      };
    };
    t17 = [isOpen, mounted];
    $[15] = isOpen;
    $[16] = mounted;
    $[17] = t16;
    $[18] = t17;
  } else {
    t16 = $[17];
    t17 = $[18];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[19] !== isOpen || $[20] !== mounted || $[21] !== reactId) {
    t18 = () => {
      if (!isOpen || !mounted) {
        return;
      }
      const modalElement_0 = dialogRef.current;
      if (!modalElement_0) {
        return;
      }
      const inertOwner = `modal-component-${reactId.replace(/[:]/g, "")}`;
      const bodyChildren = Array.from(document.body.children) as HTMLElement[];
      const exemptSelectors = ["[data-radix-popper-content-wrapper]", "[data-radix-portal]"];
      for (const element of bodyChildren) {
        const isCurrentModal = element === modalElement_0;
        const isExemptPortal = exemptSelectors.some(selector => element.matches(selector));
        if (isCurrentModal || isExemptPortal) {
          continue;
        }
        if (!element.hasAttribute("inert")) {
          element.setAttribute("inert", "");
          element.setAttribute("data-modal-inert-owner", inertOwner);
        }
      }
      return () => {
        const inertedByThisModal = document.body.querySelectorAll(`[data-modal-inert-owner="${inertOwner}"]`);
        inertedByThisModal.forEach(_temp);
      };
    };
    t19 = [isOpen, mounted, reactId];
    $[19] = isOpen;
    $[20] = mounted;
    $[21] = reactId;
    $[22] = t18;
    $[23] = t19;
  } else {
    t18 = $[22];
    t19 = $[23];
  }
  useEffect(t18, t19);
  let t20;
  let t21;
  if ($[24] !== isOpen || $[25] !== mounted) {
    t20 = () => {
      if (!isOpen || !mounted) {
        setHasFormWithRequiredFields(false);
        return;
      }
      evaluateRequiredFieldsMessage();
      const modalBody_0 = modalBodyRef.current;
      if (!modalBody_0) {
        return;
      }
      const observer_0 = new MutationObserver(() => {
        evaluateRequiredFieldsMessage();
      });
      observer_0.observe(modalBody_0, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["required", "aria-required"]
      });
      return () => observer_0.disconnect();
    };
    t21 = [evaluateRequiredFieldsMessage, isOpen, mounted];
    $[24] = isOpen;
    $[25] = mounted;
    $[26] = t20;
    $[27] = t21;
  } else {
    t20 = $[26];
    t21 = $[27];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[28] !== isOpen) {
    t22 = () => {
      if (isOpen) {
        previouslyFocusedElement.current = document.activeElement as HTMLElement;
      }
    };
    t23 = [isOpen];
    $[28] = isOpen;
    $[29] = t22;
    $[30] = t23;
  } else {
    t22 = $[29];
    t23 = $[30];
  }
  useEffect(t22, t23);
  let t24;
  let t25;
  if ($[31] !== isOpen) {
    t24 = () => {
      document.body.style.overflow = isOpen ? "hidden" : "";
      return _temp2;
    };
    t25 = [isOpen];
    $[31] = isOpen;
    $[32] = t24;
    $[33] = t25;
  } else {
    t24 = $[32];
    t25 = $[33];
  }
  useEffect(t24, t25);
  if (!mounted) {
    return null;
  }
  let t26;
  if ($[34] !== button || $[35] !== buttonCancel || $[36] !== buttonCancelText || $[37] !== buttonConfirmClassname || $[38] !== buttonConfirmText || $[39] !== buttonSubmitText || $[40] !== customButton || $[41] !== disabledConfirm || $[42] !== formAction || $[43] !== handleClose || $[44] !== handleConfirm || $[45] !== hideRequiredFieldsMessage || $[46] !== hideSubmitIcon || $[47] !== loadingConfirm || $[48] !== modals || $[49] !== onConfirm || $[50] !== shouldShowRequiredFieldsMessage || $[51] !== submitIcon || $[52] !== titleAndAria) {
    t26 = () => {
      if (!onConfirm && !formAction && !buttonCancel && !customButton) {
        return null;
      }
      const confirmTitleAndAria = titleAndAria ? `${button(onConfirm ? buttonConfirmText : buttonSubmitText)} - ${button(titleAndAria)}` : button(onConfirm ? buttonConfirmText : buttonSubmitText);
      const cancelTitleAndAria = titleAndAria ? `${button(buttonCancelText)} - ${button(titleAndAria)}` : button(buttonCancelText);
      return <section className={`modal__footer ${shouldShowRequiredFieldsMessage ? "modal__footer--with-required-message" : ""}`}>{!hideRequiredFieldsMessage && shouldShowRequiredFieldsMessage && <p className="modal__required-fields-message">{modals("requiredFieldsMessage")}</p>}<section className="modal__footer-actions">{buttonCancel && <ButtonComponent onClick={handleClose} label={buttonCancelText} title={cancelTitleAndAria} aria-label={cancelTitleAndAria} variant="outline" />}{onConfirm && <ButtonComponent onClick={handleConfirm} loading={loadingConfirm} label={buttonConfirmText} title={confirmTitleAndAria} aria-label={confirmTitleAndAria} className={buttonConfirmClassname} />}{formAction && <SubmitButtonComponent buttonLabel={buttonSubmitText} disabled={disabledConfirm} title={confirmTitleAndAria} label={confirmTitleAndAria} icon={hideSubmitIcon ? undefined : submitIcon} />}{customButton}</section></section>;
    };
    $[34] = button;
    $[35] = buttonCancel;
    $[36] = buttonCancelText;
    $[37] = buttonConfirmClassname;
    $[38] = buttonConfirmText;
    $[39] = buttonSubmitText;
    $[40] = customButton;
    $[41] = disabledConfirm;
    $[42] = formAction;
    $[43] = handleClose;
    $[44] = handleConfirm;
    $[45] = hideRequiredFieldsMessage;
    $[46] = hideSubmitIcon;
    $[47] = loadingConfirm;
    $[48] = modals;
    $[49] = onConfirm;
    $[50] = shouldShowRequiredFieldsMessage;
    $[51] = submitIcon;
    $[52] = titleAndAria;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  const renderFooter = t26;
  let t27;
  if ($[54] !== button || $[55] !== handleClose || $[56] !== t || $[57] !== title || $[58] !== titleId) {
    t27 = () => <><section className="modal__close"><button onClick={handleClose} className="btn btn--primary modal__close__button" type="button" title={button("buttonCloseModal")}>{t("close")} <X aria-hidden="true" size={20} /></button></section><section className="modal__header"><h2 id={titleId}>{title}</h2></section></>;
    $[54] = button;
    $[55] = handleClose;
    $[56] = t;
    $[57] = title;
    $[58] = titleId;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  const renderHeader = t27;
  if (!isOpen) {
    return null;
  }
  let t28;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      allowOutsideClick: true,
      tabbableOptions: {
        displayCheck: "none"
      }
    };
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  let t30;
  let t31;
  let t32;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      opacity: 0
    };
    t30 = {
      opacity: 1
    };
    t31 = {
      duration: 0.2
    };
    t32 = {
      opacity: 0
    };
    $[61] = t29;
    $[62] = t30;
    $[63] = t31;
    $[64] = t32;
  } else {
    t29 = $[61];
    t30 = $[62];
    t31 = $[63];
    t32 = $[64];
  }
  let t33;
  let t34;
  if ($[65] !== handleClose) {
    t33 = e_0 => {
      if (e_0.target === e_0.currentTarget) {
        handleClose();
      }
    };
    t34 = e_1 => {
      if (e_1.key === "Escape") {
        handleClose();
      }
    };
    $[65] = handleClose;
    $[66] = t33;
    $[67] = t34;
  } else {
    t33 = $[66];
    t34 = $[67];
  }
  let t35;
  if ($[68] !== renderHeader) {
    t35 = renderHeader();
    $[68] = renderHeader;
    $[69] = t35;
  } else {
    t35 = $[69];
  }
  let t36;
  if ($[70] !== children || $[71] !== formAction || $[72] !== renderFooter) {
    t36 = formAction ? <Form action={formAction}>{children && <section className="modal__content">{children}</section>}{renderFooter()}</Form> : <>{children && <section className="modal__content">{children}</section>}{renderFooter()}</>;
    $[70] = children;
    $[71] = formAction;
    $[72] = renderFooter;
    $[73] = t36;
  } else {
    t36 = $[73];
  }
  let t37;
  if ($[74] !== bodyStyle || $[75] !== t35 || $[76] !== t36) {
    t37 = <section className="modal__body" style={bodyStyle} ref={modalBodyRef}>{t35}{t36}</section>;
    $[74] = bodyStyle;
    $[75] = t35;
    $[76] = t36;
    $[77] = t37;
  } else {
    t37 = $[77];
  }
  let t38;
  if ($[78] !== id || $[79] !== style || $[80] !== t33 || $[81] !== t34 || $[82] !== t37 || $[83] !== titleId) {
    t38 = <motion.dialog ref={dialogRef} id={id} initial={t29} animate={t30} transition={t31} exit={t32} className="modal modal--overlay" onMouseDown={t33} onKeyDown={t34} aria-labelledby={titleId} style={style}>{t37}</motion.dialog>;
    $[78] = id;
    $[79] = style;
    $[80] = t33;
    $[81] = t34;
    $[82] = t37;
    $[83] = titleId;
    $[84] = t38;
  } else {
    t38 = $[84];
  }
  let t39;
  if ($[85] !== isTrapPaused || $[86] !== t38) {
    t39 = createPortal(<FocusTrap active={true} paused={isTrapPaused} focusTrapOptions={t28}>{t38}</FocusTrap>, document.body);
    $[85] = isTrapPaused;
    $[86] = t38;
    $[87] = t39;
  } else {
    t39 = $[87];
  }
  return t39;
};
export default ModalComponent;
function _temp(element_0) {
  element_0.removeAttribute("inert");
  element_0.removeAttribute("data-modal-inert-owner");
}
function _temp2() {
  document.body.style.overflow = "";
}